import { Fragment, useRef, useState, useEffect } from "react";
import { useMatch, useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { Button, Container, Form } from "react-bootstrap";
import { Logout, RegisterToken, RequestToken, RequestTokenWithSession } from "../../graphQL/api";
import { DmwaError } from "../../models/DmwaError";
import ToastView from "../common/ToastView";
import LoadingSpinner from "../common/LoadingSpinner";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { IsLoggedIn, clearToken, setAppId, setToken, getAppId, generateAppId } from "../../redux/tokenSlice";
import { Paths } from "../routes/Routes";
import ModalView from "../common/ModalView";
import { ApolloConsumer } from "@apollo/client";
import { clearAppState } from "../../redux/appStateSlice";
import { clearSelectedGroup } from "../../redux/navigationSlice";
import { isIos, renderBannerInstall, renderIOSBannerInstall } from "../../helpers/renderHelper";

const Login = () => {

	const loginMatch = useMatch(`${Paths.LOGIN}/:customProtocol`);

  	const [searchParams] = useSearchParams();
	const [validated, setValidated] = useState(false);
	const [showLoading, setShowLoading] = useState(false);
	const [showLogout, setShowLogout] = useState(false);
	const [error, setError] = useState<DmwaError| undefined>();
	const [showPrompt, setShowPrompt ] = useState(false)

	var geoTabDBName = searchParams.get('geoTabDBName');
	var userName = searchParams.get('userName');
	var authToken = searchParams.get('authToken');

	const customProtocolUrl = loginMatch?.params.customProtocol
	if (customProtocolUrl) {
		const newUrl = customProtocolUrl.replace("web+gridlinedriverlogin://","https://mobiledev.grindline.com/?")
		
		const customSearchParams = new URLSearchParams(new URL(newUrl).search);
		geoTabDBName = customSearchParams.get('geoTabDBName');
		userName = customSearchParams.get('userName');
		authToken = customSearchParams.get('authToken');
	}

	const isGeoTabAuthRequest = (geoTabDBName !== null) && (userName !== null) && (authToken !== null);

	const apolloClient = useRef<any>()

	const dispatch = useAppDispatch();
	const isLoggedIn = IsLoggedIn();
	const navigate = useNavigate()

	const bondedPairToken = useAppSelector(state => state.token.bondedPairToken)

	const deferredPrompt = useRef<any>();
	window.addEventListener('beforeinstallprompt', (e) => {
		// Prevents the default mini-infobar or install dialog from appearing on mobile
		e.preventDefault();
		// Save the event because you'll need to trigger it later.
		deferredPrompt.current = e;
		console.log("deferredPrompt = ", deferredPrompt)
		// Show your customized install prompt for your PWA
		setShowPrompt(true)
	});

	useEffect(() => {
		if (isGeoTabAuthRequest) {

			if (isLoggedIn && bondedPairToken) {

				dispatch(clearToken());
				dispatch(clearAppState());
				dispatch(clearSelectedGroup());

				if (apolloClient.current) {
					apolloClient.current.clearStore()
				}

				Logout(bondedPairToken, getAppId(), response => {
					loginWithGeoTab(geoTabDBName, userName, authToken)
				})
			} else {
				loginWithGeoTab(geoTabDBName, userName, authToken)				
			}
		}
	}, [])

	function loginWithGeoTab(geoTabDBName?: string | null, userName?: string | null, authToken?: string | null) {
		setShowLoading(true);

		if (geoTabDBName && userName && authToken) {
			RequestTokenWithSession(geoTabDBName, userName, authToken, response => {
				//console.log('response = ', response);
				setShowLoading(false);
				if (response instanceof DmwaError) {
					window.location.replace(`${process.env.REACT_APP_AUTH_ERROR_URL}`)
				} else {
					const token = response?.data.token;
					if(token) {
						registerToken(token);
					} else {
						//We did not get a valid token, throw an error
						setError(new DmwaError(403, "Login token is missing"))
					}
				}
			})
		}
	}

	function submitClicked(e) {
		e.preventDefault();
		e.stopPropagation();

		const form = e.currentTarget;

		setShowLoading(true);
		RequestToken(form.floatingClientId.value, form.floatingUserName.value, form.floatingPassword.value, response => {
			//console.log('response = ', response);
			setShowLoading(false);
			if (response instanceof DmwaError) {

    			window.location.replace(`${process.env.REACT_APP_AUTH_ERROR_URL}`)
				// if (response.status === 403) {
				// 	response.message = "Invalid Client Id, User name or Password";
				// }
				// setError(response);
			} else {
				const token = response?.data.token;
				if(token) {
					registerToken(token);
				} else {
					//We did not get a valid token, throw an error
					setError(new DmwaError(403, "Login token is missing"))
				}
			}
		})
	}

	function registerToken(token:string) {
		setShowLoading(true);
		RegisterToken(token, response => {
			setShowLoading(false);
			if (response instanceof DmwaError) {
				if (response.status === 403) {
					response.message = "Could not register token";
				}
				setError(response);
			} else {
				const bondedPair = response?.data.tokens;
				if(bondedPair) {
					dispatch(setToken(bondedPair))


					var appId = getAppId();

					if (appId === undefined) {
						appId = generateAppId();
						dispatch(setAppId(appId as string))
					}

					navigate(Paths.HOME)
				} else {
					//We did not get a valid token, throw an error
					setError(new DmwaError(403, "Register token is missing"))
				}
			}
		})
	}

	function toggleToast() {
		setError(undefined);
	}

	function logOut(e, client) {
		e.preventDefault();

		apolloClient.current = client

		setShowLogout(true);
	}

	function signOutClicked() {

		setShowLogout(false)

		dispatch(clearToken());
		dispatch(clearAppState());
		dispatch(clearSelectedGroup());

		if (bondedPairToken) {
			Logout(bondedPairToken, getAppId(), response => {})
		}

		if (apolloClient.current) {
			apolloClient.current.clearStore()
		}

		navigate(Paths.LOGIN);
	}


	if (isLoggedIn && !isGeoTabAuthRequest) {
		return (
			<Fragment>
				<ModalView 
					title="" 
					message="Are you sure you want to sign out?" 
					onClose={()=>setShowLogout(false)} 
					shouldShow={showLogout} 
					confirmTitle="Sign Out"
					onConfirm={()=>signOutClicked()}/>

				<Container style={{marginTop:40}}>
					<ApolloConsumer>
						{  client => 
							<Fragment>
								<p>You are already signed in.</p>
								<Button variant="warning" onClick={ e => logOut(e, client) } >Logout</Button>
							</Fragment>
						}
						
					</ApolloConsumer>
				</Container>
			</Fragment>
		)
	}

	return (
		<Fragment>
			<ToastView title="Error" message={error && error.message} shouldShow={error !== undefined} showToggle={toggleToast} />
			{ showLoading && <LoadingSpinner />}
			<Container style={{marginTop:40}}>
				<div className="row align-items-center row-cols-1">
					<div className="col col-12">
					<div className="card" style={{marginBottom: "0.35rem !important"}}>
						<div className="card-body" style={{padding: "0.35rem !important"}}>
						<div><img className="mb-0 img-fluid" src={require('../../assets/img/Gridline-DMWA-Logo.png')} width="100%" height="auto" alt="" style={{padding:"0 0"}} /></div>
						</div>
					</div>
					</div>
				</div>
				
				{ showPrompt && !isIos() &&
					<div className="row align-items-center row-cols-1" >
						{ renderBannerInstall(deferredPrompt) }
					</div>
				}
				
				<div className="row align-items-center row-cols-1" >
					<div className="col col-12 align-items-center">
					<div className="card">
						<div className="card-body" style={{padding: "0.35rem !important"}}>
						<div style={{width:"100% !important", padding:"0 0 !important"}}>
							<Form noValidate validated={validated} onSubmit={submitClicked}>
								<div className="form-floating">
									<input type="id" className="form-control" id="floatingClientId" placeholder="Client ID" />
									<label htmlFor="floatingClientId">Client ID</label>
								</div>
								<div className="form-floating">
									<input type="username" className="form-control" id="floatingUserName" placeholder="Username" />
									<label htmlFor="floatingUserName">Username</label>
								</div>
								<div className="form-floating">
									<input type="password" className="form-control" id="floatingPassword" placeholder="Password" />
									<label htmlFor="floatingPassword">Password</label>
								</div>
								<button className="w-100 btn btn-lg btn-primary" type="submit">Log in </button>
							</Form> 
						</div>
						</div>
					</div>
					</div>
				</div>
				
				<div className="row align-items-center row-cols-1" >
					{ renderIOSBannerInstall() }
				</div>
				

			</Container>
		</Fragment>

	)
}

export default Login;